import { Pencil, X } from "lucide-react";
import { useEffect, useState } from "react";

const createForm = {
    ServiceNumber: "",
    Name: "",
    MobileNumber: "",
    Role: "U",
    Status: "A",
    Password: "",
    DepartmentCode: ""
};

function UserModal({
    open,
    onClose,
    mode = "create",
    user,
    onSubmit,
    onUpdate
}) {

    const [form, setForm] = useState(createForm);
    const [modalMode, setModalMode] = useState(mode);
    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        if (user && modalMode === "view") {
            setForm({
                ServiceNumber: user.ServiceNumber || "",
                Name: user.Name || "",
                MobileNumber: user.MobileNumber || "",
                Role: user.Role || "",
                Status: user.Status || "",
                Password: user.Password || "",
                DepartmentCode: user.DepartmentCode || ""
            })
        }

        if (modalMode === "create") {
            setForm(createForm);
            setErrors({});
            setSubmitted(false);
        }
    }, [user, modalMode, open]);

    useEffect(() => {
        setModalMode(mode)
    }, [mode]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        // setForm({
        //     ...form,
        //     [e.target.name]: e.target.value
        // })

        setForm(prev => ({
            ...prev,
            [name]: value
        }));

        if (submitted) {
            setErrors(prev => ({
                ...prev,
                [name]: undefined
            }))
        }
    }

    const handleEdit = () => {
        setModalMode("edit");
    };

    const validateForm = () => {
        const newErrors = {};

        if (!form.ServiceNumber.trim()) {
            newErrors.ServiceNumber = "Service number is required";
        } else if (!/^3\d{6}$/.test(form.ServiceNumber.trim())) {
            newErrors.ServiceNumber = "Service number must start with 3 and contain 7 digits";
        }

        if (!form.Name.trim()) {
            newErrors.Name = "Name is required";
        }

        if (!form.MobileNumber.trim()) {
            newErrors.MobileNumber = "Mobile number is required"
        } else if (!/^0\d{9}$/.test(form.MobileNumber.trim())) {
            newErrors.MobileNumber = "Mobile number must be in 0XXXXXXXXX format";
        }

        if (!form.Password.trim()) {
            newErrors.Password = "Password is required"
        }

        return newErrors;
    }

    const handleCreate = () => {
        setSubmitted(true);

        const validationErrors = validateForm();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        onSubmit?.(form);
    }

    const handleUpdate = () => {
        // console.log("MOCK UPDATE USER: ", form);
        setSubmitted(true);

        const validationErrors = validateForm();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        onUpdate?.(form)
    }

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-3xl w-full max-w-[720px] mx-4 max-h-[550px] md:max-h-[90vh] overflow-y-auto py-2 md:py-4 px-5 md:px-8 border border-border shadow-2xl">
                
                <div className="py-4 md:py-6 border-b border-border">

                    {/* <p className="text-xs uppercase tracking-[3px] text-electricBlue">
                        Administration
                    </p> */}

                    <div className="flex justify-between items-center mt-2">
                        
                        <h2 className="font-bold text-2xl">
                            {
                                modalMode === "create" ? "Create User" : modalMode === "edit" ? "Edit User" : "User Details"
                            }
                        </h2>

                        <div className="flex items-center gap-3">
                            {
                                modalMode === "view" && (
                                    <Pencil 
                                        size={18}
                                        className="cursor-pointer text-electricBlue"
                                        onClick={handleEdit}
                                    />
                                )
                            }

                            <button 
                                onClick={onClose}
                                className="p-2 rounded-lg hover:bg-slate-100 transition"
                                aria-label="Close modal"
                            >
                                <X size={20} />
                            </button>
                        </div>
                    </div>
                </div>    

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                    
                    <Input 
                        label="Service Number"
                        name="ServiceNumber"
                        value={form.ServiceNumber}
                        onChange={handleChange}
                        error={submitted ? errors.ServiceNumber : undefined}
                        disabled={modalMode !== "create"}
                    />

                    <Input 
                        label="Name"
                        name="Name"
                        value={form.Name}
                        onChange={handleChange}
                        error={submitted ? errors.Name : undefined}
                        disabled={modalMode === "view"}
                    />

                    <Input 
                        label="Mobile Number"
                        name="MobileNumber"
                        value={form.MobileNumber}
                        onChange={handleChange}
                        error={submitted ? errors.MobileNumber : undefined}
                        disabled={modalMode === "view"}
                    />

                    <Input 
                        label="Password"
                        name="Password"
                        value={form.Password}
                        onChange={handleChange}
                        error={submitted ? errors.Password : undefined}
                        disabled={modalMode === "view"}
                    />

                    <Select 
                        label="Role"
                        name="Role"
                        value={form.Role}
                        onChange={handleChange}
                        disabled={modalMode === "view"}
                        options={[
                            {
                                value: "A",
                                label: "Admin"
                            },
                            {
                                value: "U",
                                label: "User"
                            }
                        ]}
                    />

                    <Select 
                        label="Status"
                        name="Status"
                        value={form.Status}
                        onChange={handleChange}
                        disabled={modalMode === "view"}
                        options={[
                            {
                                value: "A",
                                label: "Active"
                            },
                            {
                                value: "I",
                                label: "Inactive"
                            }
                        ]}
                    />

                    <Input 
                        label="Department Code"
                        name="DepartmentCode"
                        value={form.DepartmentCode}
                        onChange={handleChange}
                        disabled={modalMode === "view"}
                    />

                </div>

                <div className="flex flex-col-reverse md:flex-row justify-end gap-3 mt-6 px-4 md:px-6 pb-4 md:pb-6">
                    <button
                        onClick={onClose}
                        className="w-full md:w-auto border px-5 py-3 rounded-xl"
                    >
                        Cancel
                    </button>

                    {
                        modalMode === "create" && (
                            <button
                                onClick={handleCreate}
                                className="w-full md:w-auto px-5 py-3 rounded-xl bg-deepNavy text-white hover:bg-navy transition"
                            >
                                Create
                            </button>
                        )
                    }

                    {
                        modalMode === "edit" && (
                            <button
                                onClick={handleUpdate}
                                className="w-full md:w-auto px-5 py-3 rounded-xl bg-deepNavy text-white hover:bg-navy transition"
                            >
                                Update
                            </button>
                        )
                    }
                </div>
            </div>
        </div>
    )
}

const Input = ({ label, error, ...props}) => (
    <div className="flex flex-col gap-1">
        <label className="text-sm font-medium text-textSecondary">{label}</label>

        <input 
            {...props}
            className={`w-full px-4 py-1.5 md:py-3 rounded-xl border bg-white outline-none 
            ${
                error ? "border-red-500 focus:ring-4 focus:ring-red-100" : "border-border focus:border-electricBlue focus:ring-4 focus:ring-electricBlue/10"
            }
            disabled:bg-slate-100 disabled:text-slate-500`}
        />

        {error && (
            <span className="text-xs text-red-500">
                {error}
            </span>
        )}
    </div>
);

const Select = ({
    label,
    options,
    ...props
}) => {
    return (
        <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-textSecondary">
                {label}
            </label>

            <select
                {...props}
                className="w-full px-4 py-1.5 md:py-3 rounded-xl border border-border bg-white focus:border-electricBlue focus:ring-4 focus:ring-electricBlue/10 outline-none disabled:bg-slate-100 disabled:text-slate-500"
            >
                {
                    options.map(option => (
                        <option
                            key={option.value}
                            value={option.value}
                        >
                            {option.label}
                        </option>
                    ))
                }
            </select>
        </div>
    )
}

export default UserModal;